<template>
  <div class="v2026-sch v2026-sch-bookings">

    <div class="v2026-sch-section-head">
      <span class="v2026-sch-section-label">ตารางงานทั้งหมด</span>
    </div>
    <v-select v-if="!lockMaidPicker" class="v2026-sch-maidpick"
              :components="selectComponents"
              :options="professionals"
              :filterable="false"
              label="full_name"
              placeholder="ค้นหาแม่บ้าน (รหัส/ชื่อ)"
              :value="selectedMaid"
              :disabled="lockMaidPicker"
              @search="(search, loading) => $emit('search', search, loading)"
              @input="maid => $emit('select-maid', maid)">
      <template slot="no-options">พิมพ์รหัสหรือชื่อคุณแม่บ้าน</template>
      <!-- ID first: admins scan the list by code. -->
      <template slot="option" slot-scope="option">
        <span class="v2026-sch-maidpick__id">ID:{{ option.id }}</span>
        <span class="v2026-sch-maidpick__name" :title="option.full_name">{{ option.full_name }}</span>
      </template>
      <template slot="selected-option" slot-scope="option">
        <span class="v2026-sch-maidpick__id">ID:{{ option.id }}</span>
        <span class="v2026-sch-maidpick__name" :title="option.full_name">{{ option.full_name }}</span>
      </template>
    </v-select>

    <template v-if="selectedMaid">
      <month-calendar-v2026 v-if="useCalendarComponent"
                            :month="scheduleMonth"
                            :bookings-by-day="ordersByDayMap"
                            :selected-date="selectedScheduleDay"
                            :show-create-button="false"
                            @month-change="ym => $emit('month-change', ym)"
                            @select="dateStr => $emit('select-day', dateStr)"
                            @create="$emit('create')" />
      <!-- Original inline calendar, preserved 1:1 as the reversible fallback. -->
      <div v-else class="v2026-sch-card v2026-sch-cal">
        <div class="v2026-sch-cal__head">
          <button type="button" class="v2026-sch-icon-btn" @click="$emit('prev-month')" aria-label="เดือนก่อนหน้า">
            <span class="v2026-sch-mask v2026-sch-cal__nav" aria-hidden="true"
                  :style="maskStyle(icons.chevronLeft)"></span>
          </button>
          <span class="v2026-sch-cal__title">{{ thaiMonthLabel }}</span>
          <button type="button" class="v2026-sch-icon-btn" @click="$emit('next-month')" aria-label="เดือนถัดไป">
            <span class="v2026-sch-mask v2026-sch-cal__nav" aria-hidden="true"
                  :style="maskStyle(icons.chevronRight)"></span>
          </button>
        </div>
        <div class="v2026-sch-cal__weekdays">
          <span v-for="wd in weekdays" :key="wd" class="v2026-sch-cal__weekday">{{ wd }}</span>
        </div>
        <div class="v2026-sch-cal__grid">
          <template v-for="cell in calendarCells">
            <span v-if="cell.blank" :key="cell.key"
                  class="v2026-sch-cal__cell v2026-sch-cal__cell--empty" aria-hidden="true"></span>
            <button v-else :key="cell.dateStr" type="button"
                    class="v2026-sch-cal__cell"
                    :class="{ 'is-selected': cell.dateStr === selectedScheduleDay }"
                    @click="$emit('select-cell', cell)">
              <span class="v2026-sch-cal__num">{{ cell.day }}</span>
              <span v-if="cell.count" class="v2026-sch-cal__dots" aria-hidden="true">
                <span v-for="n in Math.min(cell.count, 3)" :key="n"
                      class="v2026-sch-cal__dot v2026-sch-cal__dot--accent"
                      :class="{ 'v2026-sch-cal__dot--more': cell.count > 3 && n === 3 }"></span>
              </span>
            </button>
          </template>
        </div>
      </div>

      <loading-v2026 v-if="scheduleLoading" label="กำลังโหลดตารางงาน" />

      <template v-else-if="selectedScheduleDay">
        <div class="v2026-sch-section-head">
          <span class="v2026-sch-section-label">วันที่เลือก ({{ selectedDayOrders.length }})</span>
        </div>
        <div v-if="!selectedDayOrders.length" class="v2026-sch-note__empty">ไม่มีงานในวันนี้</div>
        <BookingCardV2026 v-for="o in selectedDayOrders" :key="o.id" :order="o" />
      </template>
      <div v-else class="v2026-sch-note__empty">เลือกวันที่เพื่อดูงาน</div>
    </template>
    <div v-else class="v2026-sch-note__empty">เลือกแม่บ้านเพื่อดูตารางงาน</div>

    <button type="button" class="v2026-sch-cal__create" @click="$emit('create')">
      <span class="v2026-sch-mask v2026-sch-cal__create-ico" aria-hidden="true"
            :style="maskStyle(icons.createBooking)"></span>
      สร้างรายการจอง
    </button>

    <!-- data-subtab-anchor: CustomerPanelV2026's "การจองล่าสุด" sub-tab scrolls to this element. -->
    <div v-if="latestOrder" class="v2026-sch-lastorder" data-subtab-anchor="latest-order">
      <div class="v2026-sch-section-head">
        <span class="v2026-sch-section-label">การจองบริการล่าสุด</span>
      </div>
      <BookingCardV2026 variant="card" :order="latestOrder" :alwaysShowDate="true">
        <!-- A dateless booking (is_no_datetime_booking) leaves the date row blank and never reaches a
             calendar — say so instead of leaving admins to search for it. -->
        <div v-if="!latestOrder.cleaning_date" class="v2026-sch-lastorder__tags">
          <span class="v2026-sch-tag v2026-sch-tag--warn"
                v-b-tooltip.hover.top
                title="ลูกค้ายังไม่ได้ระบุวันทำงาน การจองนี้จึงไม่แสดงในปฏิทิน">ยังไม่ระบุวันที่</span>
        </div>
        <template v-if="showAssignedMaid && latestOrder.professional">
          <div class="v2026-sch-lastorder__maid">
            <img class="v2026-sch-lastorder__avatar"
                 :src="latestOrder.professional.picture_url || placeholderAvatar" alt="">
            <div class="v2026-sch-lastorder__info">
              <div>
                <a class="v2026-sch-link" target="_blank"
                   :href="'https://admin-test.beneat.co/professional/' + latestOrder.professional.id + '/profile'">ID:{{ latestOrder.professional.id }} {{ latestOrder.professional.full_name }}</a>
                <span v-if="latestOrder.professional.generation_id" class="v2026-sch-lastorder__gen">(Gen {{ latestOrder.professional.generation_id }})</span>
              </div>
              <div class="v2026-sch-lastorder__tel">Tel: {{ latestOrder.professional.phone }}</div>
            </div>
          </div>
          <router-link v-if="latestOrder.conversation_id"
                       :to="`/conversations/${latestOrder.conversation_id}`" target="_blank"
                       class="v2026-sch-lastorder__chat">แชทกับคุณแม่บ้าน</router-link>
        </template>
      </BookingCardV2026>
    </div>

  </div>
</template>

<script>
import LoadingV2026 from './LoadingV2026.vue'
import MonthCalendarV2026 from '../calendar/MonthCalendarV2026.vue'
import BookingCardV2026 from './BookingCardV2026.vue'

const MaidPickOpenIndicator = {
  render: h => h('span', [h('i', { class: 'fas fa-chevron-down', attrs: { 'aria-hidden': 'true' } })])
}

// Per-type behaviour. Add a row to extend; 2, 3 and 4 are reserved.
const TYPE_REGISTRY = {
  0: { name: 'customer', showAssignedMaid: true, lockMaidPicker: false },
  1: { name: 'professional', showAssignedMaid: false, lockMaidPicker: true }
}

export default {
  name: 'schedule-tab-v2026',
  components: {
    LoadingV2026,
    MonthCalendarV2026,
    BookingCardV2026
  },
  props: {
    type: {
      type: Number,
      default: 0
    },
    professionals: {
      type: Array,
      default: () => []
    },
    selectedMaid: {
      type: Object,
      default: null
    },
    scheduleMonth: {
      // moment() object, kept opaque — only passed down to the calendar and the label.
      type: Object,
      default: null
    },
    ordersByDayMap: {
      type: Object,
      default: () => ({})
    },
    selectedScheduleDay: {
      type: String,
      default: null
    },
    scheduleLoading: {
      type: Boolean,
      default: false
    },
    selectedDayOrders: {
      type: Array,
      default: () => []
    },
    latestOrder: {
      type: Object,
      default: null
    },
    useCalendarComponent: {
      type: Boolean,
      default: true
    },
    icons: {
      type: Object,
      default: () => ({})
    },
    weekdays: {
      type: Array,
      default: () => []
    },
    calendarCells: {
      type: Array,
      default: () => []
    },
    thaiMonthLabel: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      placeholderAvatar: 'https://beneat.co/static/img/unknown.png',
      selectComponents: { OpenIndicator: MaidPickOpenIndicator }
    }
  },
  computed: {
    // Falls back to customer for an unknown code.
    typeConfig () {
      return TYPE_REGISTRY[this.type] || TYPE_REGISTRY[0]
    },
    showAssignedMaid () {
      return this.typeConfig.showAssignedMaid
    },
    lockMaidPicker () {
      return this.typeConfig.lockMaidPicker
    }
  },
  methods: {
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    }
  }
}
</script>

<style scoped>
.v2026-sch {
  --v2026-color-main: #31C1D7;
  --v2026-color-main-soft: #31C1D714;
  --v2026-color-icon-idle: #66696C;
  --v2026-link: #007bff;
  --v2026-surface: #ffffff;
  --v2026-surface-sunken: #F8FAFC;
  --v2026-surface-hover: #e9ecf1;
  --v2026-border: #e4e7ec;
  --v2026-text: #101828;
  --v2026-text-secondary: #667085;
  --v2026-text-muted: #98a2b3;
  --v2026-icon: #475467;
  --v2026-r-card: 12px;
  --v2026-r-box: 14px;
  --v2026-r-tab: 10px;
  --v2026-sp-1: 4px;
  --v2026-sp-2: 8px;
  --v2026-sp-3: 12px;
  --v2026-sp-4: 16px;
  --v2026-sp-5: 20px;
  --v2026-sp-6: 24px;
  --v2026-fs-section: 13px;
  --v2026-fs-body: 14px;
  --v2026-fs-meta: 12px;
  --v2026-fs-name: 16px;
}

.v2026-sch-bookings {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-3);

  padding-bottom: 96px;
}

.v2026-sch-card {
  background: var(--v2026-surface-sunken);
  border-radius: var(--v2026-r-card);
}

.v2026-sch-mask {
  display: inline-block;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-icon);
}

.v2026-sch-section-label {
  font-size: var(--v2026-fs-section);
  font-weight: 600;
  color: var(--v2026-text-secondary);
}
.v2026-sch-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.v2026-sch-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
}
.v2026-sch-icon-btn:hover {
  background: var(--v2026-surface-hover);
}
.v2026-sch-icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-sch-link {
  color: var(--v2026-link);
  text-decoration: none;
  font-size: var(--v2026-fs-body);
}
.v2026-sch-link:hover {
  text-decoration: underline;
}
.v2026-sch-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 3px;
}

.v2026-sch-note__empty {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-muted);
  padding: var(--v2026-sp-1) 0;
}

.v2026-sch-maidpick {
  margin-bottom: var(--v2026-sp-3);
  font-size: var(--v2026-fs-body);
}

.v2026-sch-maidpick ::v-deep .vs__dropdown-toggle {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  padding: var(--v2026-sp-2) var(--v2026-sp-3);
  min-height: 38px;
  background: var(--v2026-surface-sunken);
  border: 1px solid var(--v2026-border);
  border-radius: var(--v2026-r-tab);
}
/* Magnifier has to be a pseudo-element: vue-select's `header` slot renders outside
   .vs__dropdown-toggle. Decorative only. */
.v2026-sch-maidpick ::v-deep .vs__dropdown-toggle::before {
  content: "\f002";
  font-family: "Font Awesome 6 Free";
  font-weight: 900;
  flex: 0 0 auto;
  font-size: 14px;
  line-height: 1;
  color: var(--v2026-text-secondary);
  pointer-events: none;
}
.v2026-sch-maidpick ::v-deep .vs__dropdown-toggle:focus-within {
  border-color: var(--v2026-color-main);
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
/* `vs--open` lands on the v-select ROOT, the same node carrying .v2026-sch-maidpick.
   MUST stay after :focus-within: identical specificity, so source order decides. */
.v2026-sch-maidpick.vs--open ::v-deep .vs__dropdown-toggle {
  border-color: var(--v2026-color-main);
  border-bottom-color: transparent;
  border-radius: var(--v2026-r-tab) var(--v2026-r-tab) 0 0;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-sch-maidpick ::v-deep .vs__selected-options {
  padding: 0;
  min-width: 0;
  flex-wrap: nowrap;
  overflow: hidden;
}
.v2026-sch-maidpick ::v-deep .vs__search,
.v2026-sch-maidpick ::v-deep .vs__search:focus {
  margin: 0;
  padding: 0;
  color: var(--v2026-text);
  font-size: var(--v2026-fs-body);
}
.v2026-sch-maidpick ::v-deep .vs__search::placeholder {
  color: var(--v2026-text-secondary);
  opacity: 1;
}
.v2026-sch-maidpick ::v-deep .vs__selected {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  margin: 0;
  padding: 0;
  background-color: transparent;
  border-color: transparent;
  color: var(--v2026-text);
  font-size: var(--v2026-fs-body);
}
.v2026-sch-maidpick ::v-deep .vs__actions {
  padding: 0;
  color: var(--v2026-icon);
}
.v2026-sch-maidpick ::v-deep .vs__open-indicator,
.v2026-sch-maidpick ::v-deep .vs__clear {
  fill: var(--v2026-icon);
}
.v2026-sch-maidpick ::v-deep .vs__open-indicator {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  line-height: 1;
  color: var(--v2026-text-muted);
}
.v2026-sch-maidpick.vs--open ::v-deep .vs__open-indicator {
  transform: none;
}

.v2026-sch-maidpick ::v-deep .vs__dropdown-menu {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-1);
  padding: var(--v2026-sp-2);
  max-height: 260px;
  overflow-y: auto;
  background: var(--v2026-surface);
  border: 1px solid var(--v2026-color-main);
  border-top-color: transparent;
  border-radius: 0 0 var(--v2026-r-tab) var(--v2026-r-tab);
  box-shadow: 0 6px 14px 0 rgba(0, 0, 0, 0.16);
  z-index: 1000;
}
.v2026-sch-maidpick ::v-deep .vs__dropdown-menu::-webkit-scrollbar {
  width: 6px;
}
.v2026-sch-maidpick ::v-deep .vs__dropdown-menu::-webkit-scrollbar-thumb {
  background: var(--v2026-color-main);
  border-radius: 3px;
}

/* display:flex is required — vue-select ships display:block. */
.v2026-sch-maidpick ::v-deep .vs__dropdown-option {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  padding: var(--v2026-sp-2) var(--v2026-sp-3);
  border-radius: var(--v2026-r-box);
  background: var(--v2026-surface);
  color: var(--v2026-text);
  font-size: var(--v2026-fs-section);
  line-height: 1.45;
  white-space: nowrap;
  cursor: pointer;
}
/* !important is MANDATORY: assets/beneat.scss:308 sets the background !important and
   leaves vue-select's white text behind it — the invisible row. Do not remove. */
.v2026-sch-maidpick ::v-deep .vs__dropdown-option--highlight {
  background: var(--v2026-surface-hover) !important;
  color: var(--v2026-text);
  box-shadow: inset 0 0 0 1px var(--v2026-color-main);
}
/* Contrast repair: the muted id is unreadable on the highlight fill. */
.v2026-sch-maidpick ::v-deep .vs__dropdown-option--highlight .v2026-sch-maidpick__id {
  color: var(--v2026-icon);
}
/* --highlight above wins over this on purpose: it is the keyboard cursor and must
   always be locatable. */
.v2026-sch-maidpick ::v-deep .vs__dropdown-option--selected {
  background: var(--v2026-color-main-soft);
}
/* Tick is a pseudo-element because vue-select's `option` slot-scope does not expose
   "is this selected" — the only handle is the class the library already emits. */
.v2026-sch-maidpick ::v-deep .vs__dropdown-option--selected::after {
  content: "\f00c";
  font-family: "Font Awesome 6 Free";
  font-weight: 900;
  flex: 0 0 auto;
  margin-left: auto;
  font-size: var(--v2026-fs-section);
  line-height: 1;
  color: var(--v2026-color-main);
}

.v2026-sch-maidpick ::v-deep .vs__no-options {
  padding: var(--v2026-sp-3);
  text-align: center;
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-secondary);
}

/* Slot content is compiled in this component's scope, so no ::v-deep below. */
.v2026-sch-maidpick__id {
  flex: 0 0 auto;
  color: var(--v2026-text-secondary);
  font-weight: 400;
}
.v2026-sch-maidpick__name {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--v2026-text);
  font-weight: 600;
}

.v2026-sch-cal__create {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--v2026-sp-2);
  width: 100%;
  margin-top: var(--v2026-sp-3);
  padding: var(--v2026-sp-3);
  border: 1px solid var(--v2026-color-main);
  border-radius: 8px;
  background: rgba(49, 193, 215, 0.08);
  color: var(--v2026-color-main);
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  cursor: pointer;
}
.v2026-sch-cal__create:hover { background: rgba(49, 193, 215, 0.18); }
.v2026-sch-cal__create-ico {
  width: 18px;
  height: 18px;
  background-color: currentColor;
}

.v2026-sch-lastorder { margin-top: var(--v2026-sp-4); }
.v2026-sch-lastorder__tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--v2026-sp-1);
}
.v2026-sch-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px var(--v2026-sp-2);
  border-radius: 999px;
  font-size: var(--v2026-fs-meta);
  font-weight: 600;
  line-height: 18px;
  cursor: help;
}
/* Same amber as the "ใหม่" chip in CustomerScheduleV2026. */
.v2026-sch-tag--warn { background: #fef0c7; color: #93370d; }
.v2026-sch-lastorder__maid {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-3);
  margin-top: var(--v2026-sp-3);
  padding-top: var(--v2026-sp-3);
  border-top: 1px solid var(--v2026-border);
}
.v2026-sch-lastorder__avatar {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}
.v2026-sch-lastorder__info {
  min-width: 0;
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text);
}
.v2026-sch-lastorder__gen { color: var(--v2026-text-secondary); }
.v2026-sch-lastorder__tel { color: var(--v2026-text-secondary); }
.v2026-sch-lastorder__chat {
  display: block;
  margin-top: var(--v2026-sp-3);
  padding: var(--v2026-sp-2);
  border: 1px solid var(--v2026-color-main);
  border-radius: 8px;
  background: rgba(49, 193, 215, 0.08);
  color: var(--v2026-color-main);
  font-size: var(--v2026-fs-meta);
  font-weight: 600;
  text-align: center;
  text-decoration: none;
}
.v2026-sch-lastorder__chat:hover { background: rgba(49, 193, 215, 0.18); }

.v2026-sch-cal {
  padding: var(--v2026-sp-3);
}
.v2026-sch-cal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--v2026-sp-2);
}
.v2026-sch-cal__title {
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  color: var(--v2026-text);
}
.v2026-sch-cal__nav {
  width: 18px;
  height: 18px;
  background-color: var(--v2026-icon);
}
.v2026-sch-cal__weekdays,
.v2026-sch-cal__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}
.v2026-sch-cal__weekday {
  text-align: center;
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-muted);
  padding: var(--v2026-sp-1) 0;
}
.v2026-sch-cal__cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  transition: background 150ms ease;
}
.v2026-sch-cal__cell--empty {
  cursor: default;
}
.v2026-sch-cal__cell:not(.v2026-sch-cal__cell--empty):hover {
  background: var(--v2026-surface-hover);
}
.v2026-sch-cal__cell:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-sch-cal__num {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text);
}

.v2026-sch-cal__cell.is-selected {
  box-shadow: inset 0 0 0 2px var(--v2026-color-main);
}
.v2026-sch-cal__cell.is-selected .v2026-sch-cal__num {
  color: var(--v2026-color-main);
  font-weight: 700;
}

.v2026-sch-cal__dots {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 2px;
}
.v2026-sch-cal__dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
}
.v2026-sch-cal__dot--accent {
  background: var(--v2026-color-main);
}
.v2026-sch-cal__dot--more {
  width: 6px;
  height: 6px;
  background: #1a8ea3;
}

</style>
